import React, { useState } from "react";
import { Sidebar, SidebarBody, SidebarLink } from "@/components/ui/expandable-sidebar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faBowlFood, 
  faHeartPulse, 
  faBone, 
  faDog, 
  faShieldDog, 
  faList,
  faTag,
  faSterlingSign
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "react-router-dom"; // Keep Link import even if not directly used here, SidebarLink uses it
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface CategorySidebarProps {
  activeCategory?: number;
  onSelectCategory?: (id: number) => void;
  onSelectPriceRange?: (range: string) => void;
  activePriceRange?: string;
  subCategories?: any[];
  onSelectSubCategory?: (subCategory: string) => void;
  activeSubCategory?: string;
}

const PRICE_RANGES = [
  { label: "All Prices", value: "all" },
  { label: "Under £10", value: "Under £10" },
  { label: "£10 - £20", value: "£10 - £20" },
  { label: "£20 - £50", value: "£20 - £50" },
  { label: "£50 - £100", value: "£50 - £100" },
  { label: "Over £100", value: "Over £100" }
];

const QUICK_ACCESS_CATEGORIES = [
  { name: 'Food & Treats', icon: faBowlFood, simple_id: 6 },
  { name: 'Health', icon: faHeartPulse, simple_id: 4 },
  { name: 'Training', icon: faShieldDog, simple_id: 3 },
  { name: 'Gear', icon: faDog, simple_id: 25 }, // Changed Accessories to Gear
  { name: 'Toys', icon: faBone, simple_id: 5 },
  { name: 'All Products', icon: faList, simple_id: 1 }
];


export function CategorySidebar({
  activeCategory,
  onSelectCategory,
  onSelectPriceRange,
  activePriceRange = "all",
  subCategories = [],
  onSelectSubCategory,
  activeSubCategory = "all"
}: CategorySidebarProps) {
  const [open, setOpen] = useState(true); // Keep sidebar open by default on desktop

  // Fetch active advertisements
  const { data: advertisements } = useQuery({
    queryKey: ['advertisements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('advertisements')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
  });

  // Generate category links
  const categoryLinks = QUICK_ACCESS_CATEGORIES.map(cat => ({
    label: cat.name,
    href: `/category/${cat.simple_id}`,
    icon: <FontAwesomeIcon icon={cat.icon} className="text-primary h-5 w-5 flex-shrink-0" />,
    onClick: () => onSelectCategory && onSelectCategory(cat.simple_id) // No event needed here
  }));

  // Generate price range links
  const priceLinks = PRICE_RANGES.map(range => ({
    label: range.label,
    href: "#", // Keep href as # since it triggers a state update, not navigation
    icon: <FontAwesomeIcon icon={faSterlingSign} className="text-primary h-5 w-5 flex-shrink-0" />,
    onClick: (e?: React.MouseEvent) => { // Allow optional event
      e?.preventDefault(); // Prevent default link behavior only if event exists
      onSelectPriceRange && onSelectPriceRange(range.value);
    }
  }));

  // Generate subcategory links if available
  const subcategoryLinks = [
    {
      label: "All Subcategories",
      href: "#",
      icon: <FontAwesomeIcon icon={faTag} className="text-primary h-5 w-5 flex-shrink-0" />,
      onClick: (e?: React.MouseEvent) => { // Allow optional event
        e?.preventDefault();
        onSelectSubCategory && onSelectSubCategory("all");
      }
    },
    ...(subCategories || []).map(subCat => ({
      label: subCat.name,
      href: "#",
      icon: <FontAwesomeIcon icon={faTag} className="text-primary h-5 w-5 flex-shrink-0" />,
      onClick: (e?: React.MouseEvent) => { // Allow optional event
        e?.preventDefault();
        onSelectSubCategory && onSelectSubCategory(subCat.name);
      }
    }))
  ];

  return (
    // Removed className from Sidebar, moved to SidebarBody
    <Sidebar open={open} setOpen={setOpen} animate={true}> 
        {/* Moved className here */}
        <SidebarBody className="justify-start gap-4 py-4 bg-white dark:bg-neutral-900 border-r border-gray-200 dark:border-neutral-800 shadow-sm"> 
            <div className="flex flex-col flex-1 overflow-hidden px-4">
                {/* Updated Header styling */}
                <div className="mb-6 flex items-center justify-between">
                    {open ? (
                        <div className="font-semibold text-lg text-gray-800 dark:text-gray-200">Categories</div>
                    ) : (
                        // Minimalist icon when closed
                         <div className="w-8 h-8 bg-primary/10 rounded-md flex items-center justify-center text-primary text-xs mx-auto">C</div>
                    )}
                </div>
                
                {/* Use ScrollArea for content */}
                <ScrollArea className="flex-1 -mx-4 px-4" type="hover" scrollBarClassName="w-1.5 mr-1">
                    {/* Category Links Section */}
                    <div className="flex flex-col gap-1 border-b border-gray-200 dark:border-neutral-700 pb-4 mb-4">
                        {open && <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider">Product Types</div>}
                        {categoryLinks.map((link, idx) => (
                            <SidebarLink 
                                key={`cat-${idx}`} 
                                link={link} 
                                className={cn(
                                    "px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors duration-150",
                                    activeCategory === QUICK_ACCESS_CATEGORIES[idx].simple_id 
                                        ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold" 
                                        : ""
                                )}
                            />
                        ))}
                    </div>
                    
                    {/* Subcategory Links Section */}
                    {subCategories && subCategories.length > 0 && (
                        <div className="flex flex-col gap-1 border-b border-gray-200 dark:border-neutral-700 pb-4 mb-4">
                            {open && <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider">Subcategories</div>}
                            {subcategoryLinks.map((link, idx) => (
                                <SidebarLink 
                                    key={`sub-${idx}`} 
                                    link={link}
                                    className={cn(
                                        "px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors duration-150",
                                        (idx === 0 && activeSubCategory === "all") || 
                                        (idx > 0 && subCategories[idx-1]?.name === activeSubCategory)
                                            ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold" 
                                            : ""
                                    )}
                                />
                            ))}
                        </div>
                    )}
                    
                    {/* Price Range Links Section */}
                    <div className="flex flex-col gap-1">
                        {open && <div className="px-3 pt-2 pb-1 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider">Price Range</div>}
                        {priceLinks.map((link, idx) => (
                            <SidebarLink 
                                key={`price-${idx}`} 
                                link={link}
                                className={cn(
                                    "px-3 py-2 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 transition-colors duration-150",
                                    activePriceRange === PRICE_RANGES[idx].value
                                        ? "bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold" 
                                        : ""
                                )}
                            />
                        ))}
                    </div>
                    
                    {/* Advertisement Section */}
                    {advertisements && advertisements.length > 0 && (
                        <div className="mt-6 pt-4 border-t border-gray-200 dark:border-neutral-700">
                            {open && <div className="px-3 pt-2 pb-3 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400 tracking-wider">Featured</div>}
                            {advertisements.map((ad) => (
                                <Link
                                    key={ad.id}
                                    to={ad.link_url || '#'}
                                    className="block mb-3 group"
                                >
                                    <div className="bg-gradient-to-r from-primary/10 to-primary/5 rounded-lg p-3 hover:from-primary/20 hover:to-primary/10 transition-all duration-200 border border-primary/20">
                                        {ad.image_url && open && (
                                            <img 
                                                src={ad.image_url} 
                                                alt={ad.title}
                                                className="w-full h-20 object-cover rounded-md mb-2"
                                            />
                                        )}
                                        {open ? (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1 group-hover:text-primary transition-colors">
                                                    {ad.title}
                                                </h4>
                                                {ad.description && (
                                                    <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
                                                        {ad.description}
                                                    </p>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="w-8 h-8 bg-primary/20 rounded-md flex items-center justify-center text-primary text-xs mx-auto">
                                                ★
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </ScrollArea>
            </div>
        </SidebarBody>
    </Sidebar>
  );
}
